<!--
*  TTTech nerve-ui-components
*  Copyright(c) 2021. TTTech Industrial Automation AG.
*
*  ALL RIGHTS RESERVED.
*
*  Usage of this software, including source code, netlists, documentation,
*  is subject to restrictions and conditions of the applicable license
*  agreement with TTTech Industrial Automation AG or its affiliates.
*
*  All trademarks used are the property of their respective owners.
*
*  TTTech Industrial Automation AG and its affiliates do not assume any liability
*  arising out of the application or use of any product described or shown
*  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
*  make changes, at any time, in order to improve reliability, function or
*  design.
*
*  Contact Information:
*  support@tttech-industrial.com
*
*  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
*
* -->
<template>
  <div>
    <div
      :class="[
        { 'flex-row-reverse': !isSearchEnabled },
        {
          'padding-left-7': storeModule === 'active-remote-connections' || storeModule === 'open-connections',
        },
      ]"
      class="d-lg-flex justify-space-between"
    >
      <v-text-field
        v-if="isSearchEnabled"
        :id="`${id}SearchInput`"
        ref="tableSearch"
        v-model="searchModel"
        v-resize="onResize"
        :style="searchStyle"
        :disabled="(!search || search === '') && items.length === 0"
        :label="$t('defaultTable.search')"
        :autofocus="isAutofocusEnabled"
        prepend-inner-icon="mdi-magnify"
        class="default-table-search default-search-input mr-12"
        clearable
        @keydown.esc="(searchModel = ''), searchMethod()"
        @click:clear="(searchModel = ''), searchMethod()"
        @click:prepend-inner="(localParams.page = 1), searchMethod()"
        @keydown.enter="(localParams.page = 1), searchMethod()"
      />
      <slot name="additional-actions" />
    </div>
    <div>
      <slot name="additional-info" />
    </div>
    <v-data-table
      :id="`${id}`"
      v-model="selectedRows"
      :headers="headers"
      :items="items"
      :options.sync="localParams"
      :no-data-text="emptyState"
      :calculate-widths="true"
      :show-select="checkAllItemsAction !== ''"
      :hide-default-footer="hideFooter || !items.length"
      :hide-default-header="hideDefaultHeader"
      :disable-pagination="disablePagination"
      :server-items-length="!disablePagination ? totalRows : -1"
      :footer-props="
        !disablePagination
          ? {
              'items-per-page-options': customItemsPerPage.length !== 0 ? customItemsPerPage : [10, 25, 50],
              'items-per-page-text': customItemsPerPageText || defaultRowsPerPageText,
            }
          : {}
      "
      :item-key="itemKey"
      :loading="isLoading"
      :class="{ emptyTablePadding: items.length < 1, pointer: isRowClickable }"
      fixed-header
      class="default-table scroll"
      @update:page="updateParamsAndFetch"
      @update:items-per-page="updateParamsAndFetch"
      @update:sort-by="sortElements"
      @update:sort-desc="sortElements"
    >
      <template #header="{ props }">
        <tr v-if="isFilterColumnsEnabled">
          <td v-for="header in props.headers" :key="header.text" class="filters">
            <div>
              <v-row>
                <v-col>
                  <v-text-field
                    v-if="header.text !== 'human_readable_timestamp'"
                    v-model="filter[header.value]"
                    :placeholder="filterPlaceHolder"
                    class="filter"
                    @keydown.esc="(filter[header.value] = {}), updateParamsAndFetch()"
                    @click:clear="(filter[header.value] = {}), updateParamsAndFetch()"
                    @click:prepend="updateParamsAndFetch()"
                    @keydown.enter="(localParams.page = 1), updateParamsAndFetch()"
                  />
                </v-col>
              </v-row>
            </div>
          </td>
        </tr>
      </template>
      <template v-for="h in headers" #[`header.${h.value}`]="{}">
        <span :key="`${h.text}_text`">
          {{ h.text }}
        </span>
        <v-tooltip v-if="h.info" :key="`${h.text}_tooltip`" bottom>
          <template #activator="{ on }">
            <v-icon class="cursor-help" color="primary" v-on="on"> mdi-information </v-icon>
          </template>
          <span>{{ h.info }}</span>
        </v-tooltip>
      </template>
      <template #[`header.data-table-select`]="{}">
        <v-simple-checkbox
          id="iiotCheckAllItems"
          :value="checkAllValue"
          :indeterminate="!checkAllValue"
          color="primary"
          @click="checkAllItems(selectedItems)"
        />
      </template>
      <template #item="{ item, index }">
        <tr
          :id="`${id}Row${index}`"
          :class="isItemChecked(item) ? 'selected-row' : ''"
          tabindex="0"
          @click="
            (storeModule === 'roles' ? isRowClickable && item.name !== 'Admin' : isRowClickable)
              ? rowClicked(item, index)
              : null
          "
        >
          <td v-if="isCheckBoxColumnEnabled">
            <v-simple-checkbox
              :id="`${id}Checkbox${index}`"
              :value="isItemChecked(item)"
              :disabled="!isRowClickable"
              color="primary"
              @click="isRowClickable ? rowClicked(item, index) : null"
            />
          </td>
          <td
            v-for="(header, num) in headers"
            :key="header.value"
            :class="[
              { white: header.value === 'actions' },
              storeModule === 'open-connections' ? 'truncate-no-overflow' : 'truncate',
            ]"
            :disabled="!(storeModule === 'roles' ? isRowClickable && item.name !== 'Admin' : isRowClickable)"
            :title="header.isTruncateEnabled ? item[header.value] : ''"
            :style="item.disabled ? 'color: #b2b2b1 !important' : 'color: rgba(0, 0, 0, 0.87) !important'"
          >
            <div
              v-if="isCheckBoxColumnWithoutSelectAllEnabled && num === 0"
              :class="{
                checkBoxPosition1: id === 'iiotDeployStepTwoDevicesDeploy',
                checkBoxPosition3: id === 'iiotActiveRemoteConnectionsList',
                checkBoxPosition2: id !== 'iiotDeployStepTwoDevicesDeploy' && id !== 'iiotActiveRemoteConnectionsList',
              }"
            >
              <v-simple-checkbox
                :id="`${id}Checkbox${index}`"
                :value="isItemChecked(item)"
                color="primary"
                class="ma-0 checkbox"
                @click="rowClicked(item, index)"
              />
            </div>
            <div
              v-if="
                header.component &&
                (storeModule === 'roles' ? item.name !== 'Admin' : true) &&
                (storeModule === 'users' ? item.type !== 'ldap' : true) &&
                (storeModule === 'roles' ? item.type !== 'ldap' : true) &&
                haveAccess
              "
            >
              <component
                :is="header.component.sfc"
                :id="`${id}Select`"
                v-bind="{ ...header.component.props, tableItem: item, index }"
                :bus="bus"
                :class="
                  header.component &&
                  header.component.props &&
                  header.component.props.iconsAction &&
                  header.component.props.iconsAction.length == 1
                    ? 'ml-4'
                    : ''
                "
                v-on="$listeners"
              />
            </div>
            <div v-else-if="header.length">
              {{ item[header.value].length }}
            </div>
            <div v-else>
              <div @mouseover="isTooltip($event)">
                <v-tooltip v-if="tooltip" bottom>
                  <template #activator="{ on }">
                    <div v-on="on">
                      {{ handleItem(item, header.value) }}
                    </div>
                  </template>
                  <span>{{ handleItem(item, header.value) }}</span>
                </v-tooltip>
                <div v-else>
                  {{ handleItem(item, header.value) }}
                </div>
              </div>
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
// eslint-disable-next-line import/no-unresolved, import/no-extraneous-dependencies
import Vue from 'vue';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';
// eslint-disable-next-line import/extensions
import Helper from './helpers/shared.js';

export default {
  props: {
    id: {
      type: String,
      default: '',
    },
    bus: {
      type: Object,
      default: () => new Vue(),
    },
    /**
     * Empty state string - make sure it is i18n
     */
    emptyState: {
      type: String,
      required: true,
    },
    /**
     * List of actions that will be shown inside action menu on the side
     * remove, clone, depending on the list
     */
    actionMenuItems: {
      type: Array,
      default() {
        return [
          {
            name: 'remove', // name of the action
            text: 'globals.remove', // i18n notation
            id: 'iiotLabelListActionMenuItem_',
            isAllowed: true, // permission should be implemented here - it will disable the field
            action: (item) => {
              try {
                if (this.storeModule === 'roles' && item.users.length > 0) {
                  this.$store.dispatch('utils/_api_request_handler/show_info_dialog', {
                    title: `${this.storeModule}.list.infoTitle`,
                    subTitle: this.$te(`${this.storeModule}.list.infoSubTitle`)
                      ? `${this.storeModule}.list.infoSubTitle`
                      : '',
                    callback: async () => {
                      await this.$store.dispatch(`${this.storeModule}/remove`, item);
                      if (this.deleteMessage) {
                        await this.$store.dispatch('utils/_api_request_handler/show_custom_toast', {
                          text: this.deleteMessage,
                          color: '#5cb860',
                          showClose: true,
                        });
                      }
                      this.search = '';
                      if (this.fetchData) {
                        await this.$store.dispatch(
                          `${this.storeModule}/${this.customFetchAction || 'fetch'}`,
                          this.localParams,
                        );
                      }
                    },
                  });
                  return;
                }
                this.$store.dispatch('utils/_api_request_handler/show_confirm_dialog', {
                  title: `${this.storeModule}.list.deleteConfirm`,
                  subTitle: this.$t(`${this.storeModule}.list.removeSubTitle`)
                    ? this.$t(`${this.storeModule}.list.removeSubTitle`, {
                        name: item.name,
                      })
                    : '',
                  callback: async () => {
                    await this.$store.dispatch(`${this.storeModule}/remove`, item);
                    if (this.deleteMessage) {
                      await this.$store.dispatch('utils/_api_request_handler/show_custom_toast', {
                        text: this.deleteMessage,
                        color: '#5cb860',
                        showClose: true,
                      });
                    }
                    this.search = '';
                    if (this.fetchData) {
                      await this.$store.dispatch(
                        `${this.storeModule}/${this.customFetchAction || 'fetch'}`,
                        this.localParams,
                      );
                    }
                  },
                });
              } catch (e) {
                // eslint-disable-next-line no-console
                this.$log.debug(e);
              }
            }, // action that will be called on click
          },
        ];
      },
    },
    /**
     * List of columns consumed by the vuetify table
     */
    columns: {
      type: Array,
      default: () => [],
    },
    /**
     * String representing store module that will be used for
     * actions and getters
     */
    storeModule: {
      type: String,
      default: '',
    },
    /**
     * Flag to enable/disable action menu column
     */
    isActionMenuEnabled: {
      type: Boolean,
      default: true,
    },
    /**
     * String that use as placeholder for filter in column
     */
    filterPlaceHolder: {
      type: String,
      default: 'Filter',
    },
    /**
     * String that will call custom store action instead of default fetch
     */
    customFetchAction: {
      type: String,
      default: '',
    },

    /**
     * Array values shown per page
     */
    customItemsPerPage: {
      type: Array,
      default: () => [],
    },
    customItemsPerPageText: {
      type: String,
      default: '',
    },
    /**
     * String that will call custom store getter instead of default list
     */
    customListGetter: {
      type: String,
      default: '',
    },
    /**
     * String that will call custom store getter for count instead of default count
     */
    customCountGetter: {
      type: String,
      default: '',
    },
    /**
     * Flag to enable/disable view/edit of one element from table
     */
    isRowClickable: {
      type: Boolean,
      default: true,
    },
    /**
     * Custom params object that will be passed to fetch store method if exists
     */
    params: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Flag to enable/disable pagination
     */
    disablePagination: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag that defines whether the footer will be displayed or not
     */
    hideFooter: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag that defines whether the default header will be displayed or not
     */
    hideDefaultHeader: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag to enable/disable display of checkBox column
     */
    isCheckBoxColumnEnabled: {
      type: Boolean,
      default: false,
    },

    /**
     * Flag to enable/disable display of checkBox column without select all option
     */
    isCheckBoxColumnWithoutSelectAllEnabled: {
      type: Boolean,
      default: false,
    },

    isFilterColumnsEnabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Store getter which return items that should be checked
     */
    selectedItemsGetter: {
      type: String,
      default: '',
    },
    /**
     * Item property that should be uniquie and which defines checkBox value/state
     */
    itemKey: {
      type: String,
      default: 'id',
    },
    /**
     * Store action called when row is selected
     */
    checkItemAction: {
      type: String,
      default: '',
    },
    /**
     * Store action called when click event performed on checkAll checkBox
     */
    checkAllItemsAction: {
      type: String,
      default: '',
    },
    /**
     * Store action called when search button/enter key pressed under search textBox
     */
    customSearchAction: {
      type: String,
      default: '',
    },
    /**
     * Message shown in toast after item is removed from list
     */
    deleteMessage: {
      type: String,
      default: '',
    },
    /**
     * Is autofocus enabled by default
     */
    isAutofocusEnabled: {
      type: Boolean,
      default: true,
    },
    /**
     * Is search enabled by default
     */
    isSearchEnabled: {
      type: Boolean,
      default: true,
    },
    /**
     * Used to set permission
     */
    haveAccess: {
      type: Boolean,
      default: true,
    },
    /**
     * Used to determin if data should be fetched
     */
    fetchData: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      // bus: new Vue(),
      options: {},
      searchModel:
        this.$route && this.$route.query && this.$route.query.search
          ? decodeURIComponent(this.$route.query.search)
          : '',
      search: '',
      filter: {},
      isLoading: false,
      actionMenuColumn: {
        text: '',
        value: 'actions',
        width: '50px',
        sortable: false,
        component: {
          sfc: () => import('./helpers/TableActionMenu.vue'),
          props: { actionMenuItems: this.actionMenuItems },
        },
      },
      selectedRows: [],
      maxWidth: 25,
      tooltip: true,
    };
  },
  computed: {
    /**
     * Fetch items for the table to consume from the store module either using custom list getter
     * or default getter list
     */
    items() {
      if (
        this.customListGetter &&
        typeof this.$store.getters[`${this.storeModule}/${this.customListGetter}`] === 'function'
      ) {
        return this.$store.getters[`${this.storeModule}/${this.customListGetter}`]();
      }
      return this.$store.getters[`${this.storeModule}/${this.customListGetter || 'list'}`];
    },
    /**
     * Fetch items count from store state for the appropriate state
     */
    totalRows() {
      if (
        this.customListGetter &&
        typeof this.$store.getters[
          this.customCountGetter ? `${this.storeModule}/${this.customCountGetter}` : `${this.storeModule}/count`
        ] === 'function'
      ) {
        return this.$store.getters[
          this.customCountGetter ? `${this.storeModule}/${this.customCountGetter}` : `${this.storeModule}/count`
        ]();
      }
      return this.$store.getters[
        this.customCountGetter ? `${this.storeModule}/${this.customCountGetter}` : `${this.storeModule}/count`
      ];
    },

    /**
     * Compute headers for the table to consume depending if the menu is enabled
     */
    headers() {
      if (this.isActionMenuEnabled) {
        return [...this.columns, this.actionMenuColumn];
      }
      return this.columns;
    },
    /**
     * Fetch selectedItems for the table to consume from the store module getter
     */
    selectedItems: {
      get() {
        return this.$store.getters[`${this.storeModule}/${this.selectedItemsGetter}`];
      },
      set() {},
    },
    /**
     * Property that defines value/state of checkAll checkBox
     */
    checkAllValue() {
      return this.items.every((item) => this.isItemChecked(item));
    },
    /**
     * Property that tracks changes in params prop and emits event when the props are updated
     */
    localParams: {
      get() {
        return this.params;
      },
      set(params) {
        this.$emit('params-changed', {
          ...this.localParams,
          ...params,
          search: this.search,
          filter: this.filter,
        });
      },
    },
    defaultRowsPerPageText() {
      return this.$t('defaultTable.rowsPerPage');
    },
    searchStyle() {
      return {
        '--max-width': `${this.maxWidth}%`,
      };
    },
  },
  mounted() {
    this.bus.$on('selected-all', (data) => {
      this.selectedRows = [...data];
    });

    this.bus.$on('tableAction', (data) => {
      this.$emit('tableAction', data);
    });
    this.searchMethod();
  },
  methods: {
    // tooltip will be shown only in the case when text is too big and when contains 3 dots
    isTooltip(event) {
      this.tooltip = event.target.scrollWidth > event.target.offsetWidth;
    },
    /**
     * Dispatch store action to populate adequate getter that will be consumed by the table
     */
    async fetch(params) {
      try {
        this.isLoading = true;
        await this.$store.dispatch(`${this.storeModule}/${this.customFetchAction || 'fetch'}`, params);
        this.isLoading = false;
      } catch (e) {
        // eslint-disable-next-line no-console
        this.$log.debug(e);
        this.isLoading = false;
      }
    },
    // eslint-disable-next-line func-names
    sortElements: debounce(function (params = {}) {
      this.updateParamsAndFetch(params);
    }, 100),
    /**
     * Calls fetch method with pagination params, including search term, limit and page
     * @param params (Object) - pagination params
     */
    updateParamsAndFetch(params = {}) {
      if (this.fetchData) {
        this.fetch({
          ...this.localParams,
          ...params,
          search: this.search,
          filter: this.filter,
        });
      }
      this.$emit('params-changed', {
        ...this.localParams,
        ...params,
        search: this.search,
        filter: this.filter,
      });
    },
    /**
     * Concatetinate array  value by key with , except the last place
     * @param array - array to iterate trough
     * @param key - key to access value of the each object in collection
     * @returns string
     */
    handleArrayItemType(array, key) {
      return array.reduce((acc, val, index) => `${acc} ${val[key]}${index + 1 < array.length ? ',' : ''}`, '');
    },

    /**
     * Enable user to use dot notation ('key.subKey') to
     * handle nested data of the table entity for arrays
     * (concatetinate values) and objects.
     *
     * @param item - table item
     * @param key - column key
     * @returns {string|*}
     */
    handleItem(item, key) {
      const splitKeyArray = key.split('.');
      // value is first nested property
      const value = item[splitKeyArray[0]];

      if (Array.isArray(value)) {
        return this.handleArrayItemType(value, splitKeyArray[1]);
      }
      if (Helper.isObject(value)) {
        return value[splitKeyArray[1]];
      }

      return item[splitKeyArray[0]];
    },
    /**
     * Emit event that row is clicked if checkItemAction
     * is not defined otherwise dispatch store action for selecting item
     * @param item - item/row inside datatable on which click event is performed
     */
    rowClicked(item, index) {
      try {
        if (this.checkItemAction !== '') {
          this.$store.dispatch(`${this.storeModule}/${this.checkItemAction}`, item);
        } else {
          this.bus.$emit('rowClicked', { item, index });
          this.$emit('row-clicked', { item, index });
          this.itemSelected(item);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        this.$log.debug(e);
      }
    },
    itemSelected(item) {
      const keyID = item.id ? item.id : item._id;
      this.selectedRows = this.headers[0].component ? this.selectedRows : [];
      if (this.selectedRows.includes(item)) {
        this.selectedRows = this.selectedRows.filter((row) => row.id !== keyID);
      } else {
        this.selectedRows.push(item);
      }
    },
    /**
     * Method defines value/state of checkbox for single row
     * @param item - item inside datatable that should be checked/unchecked
     */
    isItemChecked(item) {
      const itemsForSelect = this.selectedItems ? this.selectedItems : this.selectedRows;
      let result;
      if (itemsForSelect) {
        // eslint-disable-next-line array-callback-return, consistent-return
        result = itemsForSelect.find((element) => {
          // specific for the active remote connections table(requestUid is unique for the element)
          if (element.requestUid && item.requestUid) {
            return element.requestUid === item.requestUid;
          }
          if (element.connectionRequestUid && item.connectionRequestUid) {
            // active remote connections - LocalUI
            return element.connectionRequestUid === item.connectionRequestUid;
          }
          if (element.id && item.id) {
            return element.id === item.id;
          }
        });
      }
      if (result) {
        return true;
      }
      return itemsForSelect.includes(item) || itemsForSelect.includes(item[this.itemKey]);
    },
    /**
     * Method invoked when click event performed on checkAll checkBox
     * @param selectedItems - items inside datatable that
     * should be checked/unchecked by checkAll action
     */
    async checkAllItems() {
      await this.$store.dispatch(`${this.storeModule}/${this.checkAllItemsAction}`);
    },
    /**
     * Calls custom search method with params including search
     * term if customSearchAction is defined, or calls updateParamsAndFetch otherwise
     * @param params (Object) - params
     */
    searchMethod(params = {}) {
      this.search = this.searchModel
        ? encodeURIComponent(this.searchModel).replace(/[()*]/g, (c) => `%${c.charCodeAt(0).toString(16)}`)
        : this.searchModel;
      if (this.customSearchAction !== '') {
        this.searchItems({
          ...this.localParams,
          ...params,
          search: this.search,
        });
      } else {
        this.updateParamsAndFetch(params);
      }
    },
    /**
     * Dispatch store action to populate adequate getter that will be consumed by the table
     */
    async searchItems(params) {
      this.isLoading = true;
      await this.$store.dispatch(`${this.storeModule}/${this.customSearchAction}`, params);
      this.isLoading = false;
    },
    onResize() {
      if (window.innerWidth > 1264) {
        this.maxWidth = 25;
        return;
      }
      this.maxWidth = 100;
    },
  },
};
</script>

<style lang="scss">
.filter {
  min-width: 250px;
  margin-left: 2px;
}
.filters {
  border-collapse: separate;
  border-spacing: 2px 0;
  height: 30px !important;
}
.default-table-search {
  max-width: var(--max-width) !important;
}
.default-table > .v-data-table__wrapper > table > tbody > tr > td > div {
  max-width: 500px;
}
.default-table > .v-data-table__wrapper > table > tbody {
  display: table-row-group;
}

.default-table > {
  .v-data-table__wrapper {
    overflow-y: auto;
    overflow-x: auto;
    max-height: 75vh;
    table {
      width: 100%;
    }
  }

  .v-data-table__wrapper > table {
    padding-top: 0;
    border-collapse: separate;
    border-spacing: 0 3px !important;
    border-bottom: solid 1px #f0f3f5 !important;

    thead {
      tr {
        background: transparent;
        height: 30px !important;

        &:hover {
          background-color: transparent !important;
        }
      }
    }

    tr {
      height: 46px;
      background-color: rgba(251, 252, 252, 1);
      border-radius: 3px;
      &:hover {
        background-color: rgba(226, 243, 255, 0.6) !important;
      }
      &:focus {
        outline: none !important;
      }

      td {
        border: none !important;
        font-size: 14px !important;
        color: rgba(0, 0, 0, 0.87) !important;
        letter-spacing: 0.6px !important;
        font-weight: 300;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: 0.6px;
      }

      th {
        border: none !important;
        font-size: 16px !important;
        color: rgba(0, 0, 0, 0.87) !important;
        letter-spacing: 0.64px !important;
        font-weight: normal;
        padding-top: 15px !important;
      }
    }
  }
  .v-data-footer {
    border: none !important;
  }
  @media all and (max-width: 600px) {
    .v-data-footer {
      justify-content: center;
      padding-bottom: 8px;
      border: none !important;

      & .v-data-footer__select {
        margin: 0 auto;
      }

      & .v-data-footer__pagination {
        margin: 0;
        width: 100%;
      }
    }
  }
}

.truncate {
  max-width: 100%;

  div {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.truncate-no-overflow {
  max-width: 100%;

  div {
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.selected-row {
  background-color: rgba(203, 230, 249, 1) !important;
}

.emptyTablePadding {
  padding-right: 32px;
}

.cursor-help {
  cursor: help;
}

.checkBoxPosition1 {
  position: relative;
  top: 35px;
  right: 50px;
  margin-top: -33px;
}

.checkBoxPosition2 {
  position: relative;
  top: 25px;
  right: 50px;
  margin-top: -25px;
}

.checkBoxPosition3 {
  position: relative;
  top: 25px;
  right: 50px;
  margin-top: -25px;
  max-width: 35px !important;
  margin-right: auto !important;
}
.checkbox {
  max-width: 35px !important;
  margin-right: auto !important;
}

.padding-left-7 {
  padding-left: 7px;
}

.primary-tttech-background-btn {
  background-image: linear-gradient(to bottom, var(--v-primary-base), var(--v-primary-darken1));
}

.scroll {
  ::-webkit-scrollbar {
    width: 3px;
    height: 3px;
  }

  ::-webkit-scrollbar-track {
    background: silver;
  }

  ::-webkit-scrollbar-thumb {
    @extend .primary-tttech-background-btn;
  }
}
.default-table > .v-data-table__wrapper > table > tbody {
  tr td:first-child {
    border-left: solid 1px #f0f3f5 !important;
  }
  tr td:last-child {
    border-right: solid 1px #f0f3f5 !important;
  }
}

.default-table > .v-data-table__wrapper > table > thead > tr > th > span {
  text-transform: none;
}
.theme--light.v-data-table.v-data-table--fixed-header thead th {
  box-shadow: inset 0 -1px 0 rgb(0 0 0 / 8%) !important;
}
</style>
